import { useState, useEffect, useRef, useCallback, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useSearchQueue, type SearchResult } from '@/api/queue'
import { useCards, getCardMeaning, type Card } from '@/api/cards'
import { Spinner } from '@/components/feedback/Spinner'

// ============================================
// Types
// ============================================

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

type ResultItem =
  | { type: 'word'; data: SearchResult }
  | { type: 'card'; data: Card }
  | { type: 'recent'; query: string }

// ============================================
// Styles
// ============================================

const overlayStyles: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(44, 36, 22, 0.5)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '15vh',
  zIndex: 400,
}

const contentStyles: CSSProperties = {
  width: '100%',
  maxWidth: '540px',
  maxHeight: '70vh',
  backgroundColor: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-elevated)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  margin: '0 16px',
}

const headerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: 'var(--border-subtle)',
  gap: '12px',
}

const searchInputStyles: CSSProperties = {
  flex: 1,
  fontFamily: 'var(--font-body)',
  fontSize: '16px',
  color: 'var(--color-ink)',
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
}

const resultsContainerStyles: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '8px',
}

const groupLabelStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--color-ink-tertiary)',
  padding: '8px 12px 4px',
}

const resultItemStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  minHeight: '44px',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  transition: 'background-color 0.1s ease',
}

const resultItemSelectedStyles: CSSProperties = {
  ...resultItemStyles,
  backgroundColor: 'var(--color-accent-light)',
}

const resultTitleStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--color-ink)',
}

const resultSubtitleStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '12px',
  color: 'var(--color-ink-tertiary)',
}

const emptyStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  textAlign: 'center',
}

const emptyTextStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '14px',
  color: 'var(--color-ink-tertiary)',
}

const footerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  borderTop: 'var(--border-subtle)',
  backgroundColor: 'var(--color-paper)',
}

const shortcutHintStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '11px',
  color: 'var(--color-ink-tertiary)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const kbdStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '10px',
  fontWeight: 600,
  color: 'var(--color-ink-subtle)',
  backgroundColor: 'var(--color-surface)',
  border: 'var(--border-light)',
  borderRadius: '4px',
  padding: '2px 5px',
}

// ============================================
// Icons
// ============================================

function SearchIcon(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6" stroke="var(--color-ink-tertiary)" strokeWidth="1.5" />
      <path
        d="M13.5 13.5L17 17"
        stroke="var(--color-ink-tertiary)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function WordIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="2"
        y="3"
        width="12"
        height="10"
        rx="1.5"
        stroke="var(--color-ink-subtle)"
        strokeWidth="1.5"
      />
      <path
        d="M5 7h6M5 9.5h4"
        stroke="var(--color-ink-subtle)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CardIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="2"
        y="2"
        width="12"
        height="12"
        rx="2"
        stroke="var(--color-accent)"
        strokeWidth="1.5"
      />
      <path d="M2 6h12" stroke="var(--color-accent)" strokeWidth="1.5" />
    </svg>
  )
}

function ClockIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="var(--color-ink-subtle)" strokeWidth="1.5" />
      <path
        d="M8 5v3l2 2"
        stroke="var(--color-ink-subtle)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ============================================
// Utilities
// ============================================

const RECENT_SEARCHES_KEY = 'mellia-recent-searches'
const MAX_RECENT_SEARCHES = 5

function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (stored) {
      return JSON.parse(stored) as string[]
    }
  } catch {
    // Ignore parse errors
  }
  return []
}

function addRecentSearch(query: string): void {
  try {
    const recent = getRecentSearches().filter((q) => q !== query)
    recent.unshift(query)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES)))
  } catch {
    // Ignore storage errors
  }
}

// ============================================
// Component
// ============================================

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps): ReactNode {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()

  // Fetch word search results
  const { data: wordResults, isLoading: isLoadingWords } = useSearchQueue(
    'spanish', // Default language
    query
  )

  // Fetch card results (filter by query in memory for now)
  const { data: cardsData, isLoading: isLoadingCards } = useCards({})
  const cardResults = cardsData?.pages
    .flatMap((page) => page.cards)
    .filter((card) => {
      if (query.length < 2) return false
      const q = query.toLowerCase()
      const meaning = getCardMeaning(card) ?? ''
      return card.word.toLowerCase().includes(q) || meaning.toLowerCase().includes(q)
    })
    .slice(0, 5)

  // Load recent searches when opening
  useEffect(() => {
    if (isOpen) {
      setRecentSearches(getRecentSearches())
      setQuery('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Build results list
  const results: ResultItem[] = []

  // Add recent searches if no query
  if (!query && recentSearches.length > 0) {
    recentSearches.forEach((q) => {
      results.push({ type: 'recent', query: q })
    })
  }

  // Add word results
  if (wordResults && wordResults.words.length > 0) {
    wordResults.words.slice(0, 5).forEach((word) => {
      results.push({ type: 'word', data: word })
    })
  }

  // Add card results
  if (cardResults && cardResults.length > 0) {
    cardResults.forEach((card) => {
      results.push({ type: 'card', data: card })
    })
  }

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  // Handle selection
  const handleSelect = useCallback(
    (item: ResultItem) => {
      if (item.type === 'recent') {
        setQuery(item.query)
        return
      }

      if (item.type === 'word') {
        addRecentSearch(item.data.word)
        void navigate('/queue', { state: { selectedWord: item.data.word } })
        onClose()
        return
      }

      if (item.type === 'card') {
        addRecentSearch(item.data.word)
        void navigate(`/cards/${item.data.id}`)
        onClose()
        return
      }
    },
    [navigate, onClose]
  )

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [results, selectedIndex, handleSelect, onClose]
  )

  const isLoading = isLoadingWords || isLoadingCards
  const hasResults = results.length > 0
  const showEmpty = query.length >= 2 && !isLoading && !hasResults

  // Group results for rendering
  const recentItems = results.filter((r) => r.type === 'recent')
  const wordItems = results.filter((r) => r.type === 'word')
  const cardItems = results.filter((r) => r.type === 'card')

  // Calculate indices for each item
  let currentIndex = 0
  const getNextIndex = () => currentIndex++

  const animationProps = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.98 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.98 },
        transition: { duration: 0.15 },
      }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                style={overlayStyles}
                initial={prefersReducedMotion ? undefined : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0 }}
                transition={prefersReducedMotion ? undefined : { duration: 0.15 }}
                onClick={onClose}
              >
                <Dialog.Content asChild>
                  <motion.div
                    style={contentStyles}
                    {...animationProps}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={handleKeyDown}
                  >
                    <VisuallyHidden.Root asChild>
                      <Dialog.Title>Search</Dialog.Title>
                    </VisuallyHidden.Root>
                    <Dialog.Description asChild>
                      <VisuallyHidden.Root>Search for words and cards</VisuallyHidden.Root>
                    </Dialog.Description>

                    {/* Search Header */}
                    <div style={headerStyles}>
                      <SearchIcon />
                      <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search words and cards..."
                        style={searchInputStyles}
                        aria-label="Search"
                      />
                      {isLoading && <Spinner size="sm" />}
                    </div>

                    {/* Results */}
                    <div
                      style={resultsContainerStyles}
                      ref={resultsRef}
                      role="listbox"
                      aria-label="Search results"
                    >
                      {/* Recent Searches */}
                      {recentItems.length > 0 && (
                        <div>
                          <div style={groupLabelStyles}>Recent</div>
                          {recentItems.map((item) => {
                            const idx = getNextIndex()
                            return (
                              <div
                                key={`recent-${item.query}`}
                                data-index={idx}
                                style={
                                  selectedIndex === idx
                                    ? resultItemSelectedStyles
                                    : resultItemStyles
                                }
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                                role="option"
                                aria-selected={selectedIndex === idx}
                              >
                                <ClockIcon />
                                <span style={resultTitleStyles}>{item.query}</span>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Words */}
                      {wordItems.length > 0 && (
                        <div>
                          <div style={groupLabelStyles}>Words</div>
                          {wordItems.map((item) => {
                            const idx = getNextIndex()
                            const word = item.data
                            return (
                              <div
                                key={`word-${word.word}`}
                                data-index={idx}
                                style={
                                  selectedIndex === idx
                                    ? resultItemSelectedStyles
                                    : resultItemStyles
                                }
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                                role="option"
                                aria-selected={selectedIndex === idx}
                              >
                                <WordIcon />
                                <div>
                                  <div style={resultTitleStyles}>{word.word}</div>
                                  <div style={resultSubtitleStyles}>
                                    {word.meaning} ({word.sense_count} sense
                                    {word.sense_count !== 1 ? 's' : ''})
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Cards */}
                      {cardItems.length > 0 && (
                        <div>
                          <div style={groupLabelStyles}>Cards</div>
                          {cardItems.map((item) => {
                            const idx = getNextIndex()
                            const card = item.data
                            return (
                              <div
                                key={`card-${card.id}`}
                                data-index={idx}
                                style={
                                  selectedIndex === idx
                                    ? resultItemSelectedStyles
                                    : resultItemStyles
                                }
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                                role="option"
                                aria-selected={selectedIndex === idx}
                              >
                                <CardIcon />
                                <div>
                                  <div style={resultTitleStyles}>{card.word}</div>
                                  <div style={resultSubtitleStyles}>{getCardMeaning(card)}</div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Empty State */}
                      {showEmpty && (
                        <div style={emptyStyles}>
                          <p style={emptyTextStyles}>No results found for &quot;{query}&quot;</p>
                        </div>
                      )}

                      {/* Initial State */}
                      {!query && recentItems.length === 0 && (
                        <div style={emptyStyles}>
                          <p style={emptyTextStyles}>Start typing to search words and cards</p>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div style={footerStyles}>
                      <div style={shortcutHintStyles}>
                        <kbd style={kbdStyles}>↑↓</kbd>
                        <span>Navigate</span>
                      </div>
                      <div style={shortcutHintStyles}>
                        <kbd style={kbdStyles}>↵</kbd>
                        <span>Select</span>
                        <kbd style={kbdStyles}>esc</kbd>
                        <span>Close</span>
                      </div>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  )
}
